import React, { useEffect, useRef, useState } from 'react';
import { collection, query, where, orderBy, documentId, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import Hero from '../components/Hero';

const DAY_NAMES = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const DAY_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

// Local-timezone YYYY-MM-DD
function toKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function fromKey(key) {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
}

// Normalize a schedule doc to an array of stops (supports legacy single-location docs)
function getStops(entry) {
  if (!entry) return [];
  if (Array.isArray(entry.stops)) return entry.stops.filter((s) => s && s.location);
  return entry.location ? [entry] : [];
}

// Monday of the week containing `date`
function weekStart(date) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const offset = (d.getDay() + 6) % 7; // Mon=0 ... Sun=6
  d.setDate(d.getDate() - offset);
  return d;
}

function Location() {
  const [entries, setEntries] = useState(null); // { 'YYYY-MM-DD': {...} }
  const [settings, setSettings] = useState(null);
  const [weekIndex, setWeekIndex] = useState(0);
  const touchRef = useRef(null);

  useEffect(() => {
    const startKey = toKey(weekStart(new Date())); // include earlier days of this week
    async function load() {
      try {
        const q = query(
          collection(db, 'schedule'),
          where(documentId(), '>=', startKey),
          orderBy(documentId())
        );
        const [snap, settingsSnap] = await Promise.all([
          getDocs(q),
          getDoc(doc(db, 'settings', 'site')),
        ]);
        const map = {};
        snap.forEach((d) => { map[d.id] = d.data(); });
        setEntries(map);
        setSettings(settingsSnap.exists() ? settingsSnap.data() : {});
      } catch (err) {
        console.error('Failed to load schedule', err);
        setEntries({});
        setSettings({});
      }
    }
    load();
  }, []);

  // Scroll-in animation whenever the visible cards change
  useEffect(() => {
    if (!entries) return;
    const elements = document.querySelectorAll('.schedule-day');
    elements.forEach((element) => {
      element.style.opacity = '0';
      element.style.transform = 'translateY(20px)';
      element.style.transition = 'opacity 0.5s ease, transform 0.5s ease';
    });

    const animateOnScroll = () => {
      document.querySelectorAll('.schedule-day').forEach((element) => {
        if (element.getBoundingClientRect().top < window.innerHeight - 50) {
          element.style.opacity = '1';
          element.style.transform = 'translateY(0)';
        }
      });
    };
    animateOnScroll();
    window.addEventListener('scroll', animateOnScroll);
    return () => window.removeEventListener('scroll', animateOnScroll);
  }, [entries, weekIndex]);

  const loading = entries === null;
  const today = new Date();
  const todayKey = toKey(today);
  const thisWeekStart = weekStart(today);

  // Build week groups from this week through the last week that has an entry
  let weeks = [];
  if (!loading) {
    const keys = Object.keys(entries).sort();
    const lastKey = keys.length ? keys[keys.length - 1] : null;
    const lastWeekStart = lastKey ? weekStart(fromKey(lastKey)) : thisWeekStart;

    for (
      let ws = new Date(thisWeekStart);
      ws <= lastWeekStart;
      ws.setDate(ws.getDate() + 7)
    ) {
      const start = new Date(ws);
      const days = [];
      let hasEntry = false;
      for (let i = 0; i < 7; i++) {
        const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
        const key = toKey(d);
        if (entries[key]) hasEntry = true;
        days.push({ key, date: d, entry: entries[key] || null });
      }
      const isThisWeek = toKey(start) === toKey(thisWeekStart);
      // Hide past days within the current week
      const visibleDays = isThisWeek ? days.filter((d) => d.key >= todayKey) : days;

      let label;
      if (isThisWeek) label = 'THIS WEEK';
      else if (toKey(start) === toKey(new Date(thisWeekStart.getFullYear(), thisWeekStart.getMonth(), thisWeekStart.getDate() + 7))) label = 'NEXT WEEK';
      else label = `WEEK OF ${MONTHS[start.getMonth()]} ${start.getDate()}`;

      // Only show future weeks that have entries; always show this week
      if (isThisWeek || hasEntry) {
        weeks.push({ label, days: visibleDays, hasEntry });
      }
    }
  }

  const activeIndex = Math.min(weekIndex, Math.max(weeks.length - 1, 0));
  const activeWeek = weeks[activeIndex] || null;

  // Swipe left/right to change weeks
  const onTouchStart = (e) => {
    touchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const onTouchEnd = (e) => {
    if (!touchRef.current) return;
    const dx = e.changedTouches[0].clientX - touchRef.current.x;
    const dy = e.changedTouches[0].clientY - touchRef.current.y;
    touchRef.current = null;
    if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.5) return; // ignore taps & vertical scrolls
    if (dx < 0 && activeIndex < weeks.length - 1) setWeekIndex(activeIndex + 1);
    if (dx > 0 && activeIndex > 0) setWeekIndex(activeIndex - 1);
  };

  const todayEntry = !loading ? entries[todayKey] : null;
  const todayStops = getStops(todayEntry);
  const sms = settings || {};
  const smsBody = sms.smsKeyword ? encodeURIComponent(sms.smsKeyword) : '';

  return (
    <main>
      <Hero
        variant="location"
        title="WHERE TO FIND US"
        tagline="BREWING NEAR YOU"
        showSocial={false}
      />

      <section className="today-section">
        <h2>TODAY&apos;S LOCATION</h2>
        <div className="today-location-card">
          {loading ? (
            <p>Loading...</p>
          ) : todayEntry && !todayEntry.closed && todayStops.length > 0 ? (
            <>
              <p className="today-location-day">{DAY_FULL[today.getDay()]}</p>
              {todayStops.map((stop, si) => (
                <div key={si} className={si > 0 ? 'schedule-stop-divider' : undefined}>
                  <p className="today-location-name">{stop.location}</p>
                  {(stop.startTime || stop.endTime) && (
                    <p className="today-location-time">{stop.startTime}{stop.startTime && stop.endTime ? ' - ' : ''}{stop.endTime}</p>
                  )}
                  {stop.address && (
                    <>
                      <p className="today-location-address">{stop.address}</p>
                      <a
                        className="cta-button"
                        href={`https://maps.google.com/?q=${encodeURIComponent(stop.address)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Get Directions
                      </a>
                    </>
                  )}
                </div>
              ))}
            </>
          ) : todayEntry && todayEntry.closed ? (
            <>
              <p className="today-location-name">We&apos;re closed today</p>
              <p className="today-location-address">{todayEntry.note || 'See you soon — check the schedule below for our next spot!'}</p>
            </>
          ) : (
            <>
              <p className="today-location-name">No location posted for today</p>
              <p className="today-location-address">Check our Instagram for pop-up announcements.</p>
            </>
          )}
        </div>
      </section>

      <section className="location-section">
        <h2>UPCOMING SCHEDULE</h2>

        {loading && (
          <div className="schedule-loading">
            <div className="schedule-spinner" />
            <p>Loading this week&apos;s spots...</p>
          </div>
        )}

        {!loading && weeks.length > 1 && (
          <div className="week-tabs" role="tablist" aria-label="Choose a week">
            {weeks.map((week, i) => (
              <button
                key={week.label}
                role="tab"
                aria-selected={i === activeIndex}
                className={`week-tab${i === activeIndex ? ' active' : ''}`}
                onClick={() => setWeekIndex(i)}
              >
                {week.label}
              </button>
            ))}
          </div>
        )}

        {!loading && activeWeek && (
          <div
            key={activeWeek.label}
            className="schedule-week week-slide"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            {weeks.length <= 1 && <h3 className="week-label">{activeWeek.label}</h3>}
            {!activeWeek.hasEntry && (
              <p className="schedule-note">Schedule coming soon — check our Instagram for updates!</p>
            )}
            <div className="schedule-container">
              {activeWeek.days.map(({ key, date, entry }) => {
                const isToday = key === todayKey;
                const dayLine = `${DAY_NAMES[date.getDay()]} · ${MONTHS[date.getMonth()]} ${date.getDate()}`;
                if (entry && entry.closed) {
                  return (
                    <div key={key} className={`schedule-day closed${isToday ? ' today' : ''}`}>
                      {isToday && <span className="today-badge">★ TODAY</span>}
                      <h3>{dayLine}</h3>
                      <p className="location">Closed</p>
                      {entry.note && <p className="address">{entry.note}</p>}
                    </div>
                  );
                }
                const stops = getStops(entry);
                if (stops.length === 0) {
                  return (
                    <div key={key} className={`schedule-day tba${isToday ? ' today' : ''}`}>
                      {isToday && <span className="today-badge">★ TODAY</span>}
                      <h3>{dayLine}</h3>
                      <p className="location">TBA</p>
                      <p className="address">Check Instagram for updates</p>
                    </div>
                  );
                }
                return (
                  <div key={key} className={`schedule-day${isToday ? ' today' : ''}`}>
                    {isToday && <span className="today-badge">★ TODAY</span>}
                    <h3>{dayLine}</h3>
                    {stops.map((stop, si) => (
                      <div key={si} className={`schedule-stop${si > 0 ? ' schedule-stop-divider' : ''}`}>
                        <p className="location">{stop.location}</p>
                        {(stop.startTime || stop.endTime) && (
                          <p className="time">{stop.startTime}{stop.startTime && stop.endTime ? ' - ' : ''}{stop.endTime}</p>
                        )}
                        {stop.address && (
                          <p className="address">
                            {stop.address}
                            <br />
                            <a
                              className="directions-link"
                              href={`https://maps.google.com/?q=${encodeURIComponent(stop.address)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Get Directions →
                            </a>
                          </p>
                        )}
                      </div>
                    ))}
                    {entry.note && <p className="address">{entry.note}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {!loading && weeks.length > 1 && (
          <p className="swipe-hint">Swipe or tap a tab to see another week</p>
        )}

        {!loading && (
          <p className="schedule-note">*Schedule may vary. Check our Instagram for daily updates and special event locations.</p>
        )}
      </section>

      {sms.smsEnabled && sms.smsNumber && (
        <section className="sms-section">
          <h2>GET TEXT ALERTS</h2>
          <div className="sms-card">
            <p className="sms-pitch">Never miss us — get a text when we post next week&apos;s spots.</p>
            {sms.smsKeyword ? (
              <p className="sms-instructions">
                Text <span className="sms-keyword">{sms.smsKeyword}</span> to <span className="sms-number">{sms.smsNumber}</span>
              </p>
            ) : (
              <p className="sms-instructions">
                Text <span className="sms-number">{sms.smsNumber}</span> to join
              </p>
            )}
            <a className="cta-button" href={`sms:${sms.smsNumber}${smsBody ? `?&body=${smsBody}` : ''}`}>
              Sign Me Up
            </a>
            <p className="sms-fineprint">Msg &amp; data rates may apply. Reply STOP to opt out anytime.</p>
          </div>
        </section>
      )}

      <section className="event-section">
        <h2>BOOK US FOR YOUR EVENT</h2>
        <div className="event-content">
          <p>Looking to add a unique coffee experience to your next corporate event, wedding, or private party? We offer custom catering packages tailored to your needs.</p>
          <a href="mailto:events@groundedcoffeeco.com" className="cta-button">Contact Us</a>
        </div>
      </section>
    </main>
  );
}

export default Location;
